'use client'

import { i18n } from './i18n'
import { Post } from '@/payload-types'
import { Locale } from '@/providers/Locale/types'
import { formatRelativeTime, relativeTimeFromNow } from '@/utilities/formatDateTime'
import { cn } from '@/utilities/ui'
import { useEffect, useState } from 'react'

interface PublishInfo {
    showDisplayText: boolean
    showPublishedDate: boolean
    showUpdatedDate: boolean
    formattedPublishDate: string
    formattedUpdateDate: string
}

interface Props {
    publishedAt: Post['publishedAt']
    updatedAt?: Post['updatedAt']
    locale: Locale
    className?: string
    showDisplayText?: boolean
    showPublishTimeOnly?: boolean
}

function getPublishInfo({ publishedAt, updatedAt, locale, showPublishTimeOnly, showDisplayText = true }: Props): PublishInfo {
    const publishedDate = publishedAt ? new Date(publishedAt) : new Date(0)
    const updatedDate = updatedAt ? new Date(updatedAt) : publishedDate
    const fiveMinutesInMs = 1000 * 60 * 5
    const twentyFourHoursInMs = 1000 * 60 * 60 * 24

    const relativePublishedTime = relativeTimeFromNow(publishedAt)
    const relativeUpdateTime = relativeTimeFromNow(updatedAt)

    const isUpdatedDateAfterPublishingDate =
        updatedDate.getTime() > publishedDate.getTime() && updatedDate.getTime() - publishedDate.getTime() > fiveMinutesInMs
    const isRelativeTimeEqual =
        relativePublishedTime && relativeUpdateTime && relativeUpdateTime[1] === relativePublishedTime[1] && relativeUpdateTime[0] === relativePublishedTime[0]
    const isUpdatedWithinLast24Hours = (updatedDate?.getTime() || 0) > new Date().getTime() - twentyFourHoursInMs

    // Show published date as often as possible
    let showPublishedDate = true
    let showUpdatedDate = false

    // Only append the updated date if one of the following occurs
    // - Updated date occurs after the published date and has at least 5 minute gap between published and updated date
    // - When the relative dates are displayed, it does not match the published date
    // - The update occurred within the last 24 hours
    // - showPublishTimeOnly flag is not set
    if (isUpdatedDateAfterPublishingDate && !isRelativeTimeEqual && isUpdatedWithinLast24Hours && !showPublishTimeOnly) {
        showUpdatedDate = true
    }

    // Use the updated date instead of the published date if
    // - Updated date occurs after the published date
    // - Updated date is more than 24 hours ago
    // - showPublishTimeOnly flag is not set
    if (isUpdatedDateAfterPublishingDate && !isUpdatedWithinLast24Hours && !showPublishTimeOnly) {
        showPublishedDate = false
        showUpdatedDate = true
    }

    return {
        showDisplayText,
        showUpdatedDate,
        showPublishedDate,
        formattedPublishDate: formatRelativeTime(publishedAt, locale),
        formattedUpdateDate: formatRelativeTime(updatedAt, locale),
    }
}

export const PublishedDateInfo: React.FC<Props> = (props) => {
    const { locale, className } = props
    const [{ showUpdatedDate, showPublishedDate, formattedPublishDate, formattedUpdateDate, showDisplayText }, setPublishInfo] = useState<PublishInfo>({
        showDisplayText: false,
        showUpdatedDate: false,
        showPublishedDate: false,
        formattedPublishDate: '',
        formattedUpdateDate: '',
    })

    useEffect(() => {
        // Run the getPublishInfo on client since it uses date.now to prevent rehydration issues
        setPublishInfo(getPublishInfo(props))
    }, [setPublishInfo, props])

    // If both items are null/undefined, return empty
    if (!formattedPublishDate && !formattedUpdateDate) {
        return null
    }

    return (
        <div className={cn('flex text-base leading-[21px]', className)}>
            {showPublishedDate && (
                <time className='truncate' aria-label={i18n[locale].published}>
                    {showDisplayText && i18n[locale].published} {formattedPublishDate}
                </time>
            )}
            {showPublishedDate && showUpdatedDate && <span>|</span>}
            {showUpdatedDate && (
                <time className='truncate' aria-label={i18n[locale].updated}>
                    {showDisplayText && i18n[locale].updated} {formattedUpdateDate}
                </time>
            )}
        </div>
    )
}
